import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import GenericFormDrawer from '../../components/GenericFormDrawer';
import { getAuditLog } from '../../services/auditLogService';
import { formatDateTime } from '../../utils/Utils';
import { useAuditLabels, StaffBadge } from './config';

const Rows = styled.dl`
  display: grid;
  grid-template-columns: minmax(110px, auto) 1fr;
  gap: 6px 12px;
  margin: 0 0 16px;
  font-size: 13px;

  dt { color: #6B7280; }
  dd { margin: 0; color: ${({ theme }) => theme.colors.text.primary}; word-break: break-word; }
`;

const ChangesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th, td {
    text-align: left;
    padding: 6px 8px;
    border-bottom: 1px solid #E8ECF0;
    vertical-align: top;
    word-break: break-word;
  }

  th { color: #6B7280; font-weight: 600; }
`;

const Tag = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #FFF8E6;
  color: #92400E;
`;

const Details = styled.pre`
  margin: 12px 0 0;
  padding: 10px 12px;
  background: #F5F7FA;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Muted = styled.p`
  font-size: 13px;
  color: #6B7280;
  margin: 0;
`;

/**
 * AKM POC §9.1 — işlem ayrıntısı (salt okunur): İşlem Geçmişi sayfası ve çalışanın Değişiklik Geçmişi sekmesi paylaşır.
 * Alan değişiklikleri Alan / Önceki / Sonraki; maskeli değer saklandığı biçimiyle + "Maskeli" etiketi, gizli alan yalnız
 * "Değişti (Gizli)". Çekmecede kaydetme yok: düğme "Kapat". `fetchDetail` ayrıntıyı getiren uç: varsayılan yönetici ucu
 * (/v1/audit-logs/{id}); Değişiklik Geçmişi sekmesi kendi EM.Write kapılı ucunu verir (son inceleme I4).
 */
export default function AuditLogDetailDrawer({ logId, onClose, fetchDetail = getAuditLog }) {
  const { t, i18n } = useTranslation();
  const labels = useAuditLabels();
  const [detail, setDetail] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!logId) return undefined;
    let alive = true;
    setDetail(null);
    setFailed(false);
    fetchDetail(logId).then((res) => {
      if (!alive) return;
      if (!res?.data || res.data.status === false) setFailed(true);
      else setDetail(res.data.data);
    });
    return () => { alive = false; };
  }, [logId, fetchDetail]);

  const value = (v) => (v === null || v === undefined || v === '' ? '—' : v);
  const cell = (change, v) => (
    <>
      {value(v)}
      {change.masked && v ? <Tag>{t('auditLog.masked')}</Tag> : null}
    </>
  );

  let content;
  if (failed) content = <Muted>{t('auditLog.loadFailed')}</Muted>;
  else if (!detail) content = <Muted>{t('common.loading')}</Muted>;
  else {
    content = (
      <>
        <Rows>
          <dt>{t('auditLog.occurredAt')}</dt>
          <dd>{formatDateTime(detail.occurredAt, i18n.language)}</dd>
          <dt>{t('auditLog.actor')}</dt>
          <dd>{detail.actorIsSuperAdmin ? <StaffBadge>{t('auditLog.flexbeeStaff')}</StaffBadge> : value(detail.actorName)}</dd>
          <dt>{t('auditLog.subject')}</dt>
          <dd>{value(detail.subjectName)}</dd>
          <dt>{t('auditLog.category')}</dt>
          <dd>{labels.category(detail.category)}</dd>
          <dt>{t('auditLog.action')}</dt>
          <dd>{labels.action(detail.action)}</dd>
          <dt>{t('auditLog.outcome')}</dt>
          <dd>{value(labels.outcome(detail.outcome))}</dd>
          <dt>{t('auditLog.entityType')}</dt>
          <dd>{value(labels.entityType(detail.entityType))}</dd>
          <dt>{t('auditLog.entityId')}</dt>
          <dd>{value(detail.entityId)}</dd>
          <dt>{t('auditLog.ip')}</dt>
          <dd>{value(detail.ipAddress)}</dd>
          <dt>{t('auditLog.userAgent')}</dt>
          <dd>{value(detail.userAgent)}</dd>
        </Rows>
        {Array.isArray(detail.changes) && detail.changes.length > 0 && (
          <ChangesTable data-testid="audit-changes">
            <thead>
              <tr>
                <th>{t('auditLog.field')}</th>
                <th>{t('auditLog.oldValue')}</th>
                <th>{t('auditLog.newValue')}</th>
              </tr>
            </thead>
            <tbody>
              {detail.changes.map((c) => (
                <tr key={c.field}>
                  <td title={c.field}>{labels.field(c.field)}</td>
                  {c.omitted ? (
                    <td colSpan={2}>{t('auditLog.omitted')}</td>
                  ) : (
                    <>
                      <td>{cell(c, c.old)}</td>
                      <td>{cell(c, c.new)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </ChangesTable>
        )}
        {detail.details ? <Details>{JSON.stringify(detail.details, null, 2)}</Details> : null}
      </>
    );
  }

  return (
    <GenericFormDrawer
      open={Boolean(logId)}
      onClose={onClose}
      title={t('auditLog.detail')}
      schema={[]}
      initialValues={logId ? {} : null}
      onSave={async () => true}
      submitLabel={t('common.close')}
    >
      {content}
    </GenericFormDrawer>
  );
}
